// src/components/CategoryLanding.tsx
import React from "react";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  FolderOpen,
  FileText,
  X,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { OptimizedStarryBackground } from "./OptimizedStarryBackground";
import useProcessData from "@/hooks/useProcessData";

interface CategoryLandingProps {
  accessToken: string;
  businessArea: string;
  onCategorySelect: (category: string | null) => void;
  onBackToAreas: () => void;
}

const CategoryLanding: React.FC<CategoryLandingProps> = ({
  accessToken,
  businessArea,
  onCategorySelect,
  onBackToAreas
}) => {
  const { data: uncategorizedItems, loading, error, categories } =
    useProcessData(accessToken, businessArea, null);

  // Direkt redirect om inga kategorier finns men det finns okategoriserade
  if (!loading && categories.length === 0 && uncategorizedItems.length > 0) {
    onCategorySelect(null); // null → ProcessViewer direkt
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white">
        <OptimizedStarryBackground />
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="animate-spin h-12 w-12 text-orange-300" />
          <p className="text-gray-300">Laddar kategorier...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 text-white">
        <OptimizedStarryBackground />
        <div className="bg-gray-800/70 p-6 rounded-xl border border-gray-700 max-w-md w-full">
          <X className="h-10 w-10 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-center mb-4">
            Ett fel uppstod
          </h2>
          <p className="text-gray-300 text-center mb-6">{error}</p>
          <div className="flex justify-center">
            <Button onClick={onBackToAreas} className="bg-orange-500 text-white">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Tillbaka
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative text-white">
      <OptimizedStarryBackground />
      <button
        onClick={onBackToAreas}
        className="absolute left-4 top-4 bg-gray-800/70 text-orange-300 rounded-full p-2 h-10 w-10 flex items-center justify-center shadow-md z-50"
        aria-label="Tillbaka"
      >
        <ArrowLeft className="h-5 w-5" />
      </button>

      <div className="container mx-auto px-4 py-16">
        <motion.div
          className="mb-8 text-center"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-3xl font-bold mb-2">{businessArea}</h1>
          <p className="text-lg text-gray-300">
            Välj kategori eller standardprocesser
          </p>
        </motion.div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {uncategorizedItems.length > 0 && (
            <motion.div
              className="p-6 rounded-xl border border-orange-500/30 bg-gray-800/50 backdrop-blur-md hover:bg-gray-700/50 cursor-pointer"
              onClick={() => onCategorySelect(null)}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-orange-500/20 rounded-full">
                  <FileText className="h-6 w-6 text-white" />
                </div>
                <h2 className="text-xl font-semibold">Standardprocesser</h2>
              </div>
              <p className="text-gray-300 mb-4">
                Visa processer för {businessArea} utan kategori.
              </p>
              <div className="flex items-center justify-end gap-1 text-orange-300">
                Visa
                <ArrowRight className="h-4 w-4" />
              </div>
            </motion.div>
          )}

          {categories.map((category, idx) => (
            <motion.div
              key={category}
              className="p-6 rounded-xl border border-gray-700/50 bg-gray-800/50 backdrop-blur-md hover:bg-gray-700/50 cursor-pointer"
              onClick={() => onCategorySelect(category)}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + idx * 0.1 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-blue-500/20 rounded-full">
                  <FolderOpen className="h-6 w-6 text-white" />
                </div>
                <h2 className="text-xl font-semibold">{category}</h2>
              </div>
              <p className="text-gray-300 mb-4">
                Visa processer för {businessArea} inom {category}.
              </p>
              <div className="flex items-center justify-end gap-1 text-orange-300">
                Visa
                <ArrowRight className="h-4 w-4" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CategoryLanding;
